import React, { useState } from 'react';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { Button } from '../Button';
import { EffortRate, deleteEffortRate, saveEffortRate } from '../../services/erpSteeringService';
import { auftragswertCentsAusEingabe, centsAlsEingabe } from '../../services/erpSteering';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { KartenRahmen } from './KartenRahmen';

// Pflege der kalkulatorischen Eigenleistungssaetze (effort_rates).
//
// Der Name ist der Join-Schluessel: `report_history` kennt den Durchfuehrenden
// einer Begehung ausschliesslich als Text (`conductor_name`), es gibt dort
// keine user_id. Wer sich umbenennt, braucht deshalb eine ZWEITE Zeile mit dem
// neuen Namen — der alte Satz bleibt fuer die alten Begehungen richtig.
//
// „Gültig ab" ist die Historisierung: fuer eine Begehung am Tag T gilt die
// Zeile mit dem groessten `gueltig_ab <= T`. Eine Satzaenderung ist eine neue
// Zeile, keine Ueberschreibung — sonst rechnete sich die Vergangenheit
// rueckwirkend um.

interface Props {
    zustand: { daten: EffortRate[] | null; laedt: boolean; fehler: string | null };
    onGeaendert: () => void;
}

const inputClass =
    'px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 outline-none transition-all';
const labelClass = 'text-[11px] font-bold text-slate-500 uppercase tracking-widest ml-1';

interface Entwurf {
    coordinatorName: string;
    satz: string;
    gueltigAb: string;
}

const leererEntwurf = (): Entwurf => ({
    coordinatorName: '',
    satz: '',
    gueltigAb: new Date().toISOString().slice(0, 10),
});

export const EffortRatesCard: React.FC<Props> = ({ zustand, onGeaendert }) => {
    const [neu, setNeu] = useState<Entwurf>(leererEntwurf);
    const [busy, setBusy] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);
    const [bearbeitet, setBearbeitet] = useState<Record<string, string>>({});

    const anlegen = async () => {
        const cents = auftragswertCentsAusEingabe(neu.satz);
        if (!neu.coordinatorName.trim() || cents === null) {
            setFehler('Name und ein gültiger Satz sind Pflicht.');
            return;
        }
        setBusy(true);
        setFehler(null);
        try {
            await saveEffortRate({
                coordinatorName: neu.coordinatorName,
                satzJeBegehungCents: cents,
                gueltigAb: neu.gueltigAb,
            });
            setNeu(leererEntwurf());
            onGeaendert();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusy(false);
        }
    };

    const speichern = async (zeile: EffortRate) => {
        const cents = auftragswertCentsAusEingabe(
            bearbeitet[zeile.id] ?? centsAlsEingabe(zeile.satzJeBegehungCents),
        );
        if (cents === null) {
            setFehler('Bitte einen gültigen Satz eingeben (z. B. 180,00).');
            return;
        }
        setBusy(true);
        setFehler(null);
        try {
            await saveEffortRate({
                id: zeile.id,
                coordinatorName: zeile.coordinatorName,
                userId: zeile.userId,
                satzJeBegehungCents: cents,
                satzJePlanungsstundeCents: zeile.satzJePlanungsstundeCents ?? null,
                gueltigAb: zeile.gueltigAb,
                note: zeile.note,
            });
            onGeaendert();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusy(false);
        }
    };

    const entfernen = async (id: string) => {
        setBusy(true);
        setFehler(null);
        try {
            await deleteEffortRate(id);
            onGeaendert();
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4">
            <p className="text-xs text-slate-500">
                Kalkulatorischer Satz je Begehung und Koordinator. Der <strong>Name</strong> muss dem
                Durchführenden im Berichtsarchiv entsprechen — danach wird zugeordnet. Eine Satzänderung ist eine
                neue Zeile mit späterem „gültig ab“, keine Korrektur der alten.
            </p>

            {fehler && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{fehler}</div>
            )}

            {/* ── Neue Zeile ── */}
            <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3">
                <div>
                    <label className={labelClass} htmlFor="er-name">Koordinator (Name)</label>
                    <input
                        id="er-name"
                        className={`${inputClass} block w-56`}
                        value={neu.coordinatorName}
                        onChange={(e) => setNeu({ ...neu, coordinatorName: e.target.value })}
                    />
                </div>
                <div>
                    <label className={labelClass} htmlFor="er-satz">Satz je Begehung (EUR)</label>
                    <input
                        id="er-satz"
                        inputMode="decimal"
                        className={`${inputClass} block w-36 text-right tabular-nums`}
                        placeholder="180,00"
                        value={neu.satz}
                        onChange={(e) => setNeu({ ...neu, satz: e.target.value })}
                    />
                </div>
                <div>
                    <label className={labelClass} htmlFor="er-ab">Gültig ab</label>
                    <input
                        id="er-ab"
                        type="date"
                        className={`${inputClass} block`}
                        value={neu.gueltigAb}
                        onChange={(e) => setNeu({ ...neu, gueltigAb: e.target.value })}
                    />
                </div>
                <Button onClick={() => void anlegen()} disabled={busy} icon={<Plus className="h-4 w-4" />}>
                    Satz hinzufügen
                </Button>
            </div>

            <KartenRahmen
                laedt={zustand.laedt}
                fehler={zustand.fehler}
                leer={!!zustand.daten && zustand.daten.length === 0}
                leerText="Noch kein Satz gepflegt — die Eigenleistung wird dann überall mit 0 EUR gerechnet."
            >
                {zustand.daten && (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-[11px] uppercase tracking-widest text-slate-500">
                                    <th className="py-2 pr-3 font-bold">Koordinator</th>
                                    <th className="py-2 pr-3 font-bold">Satz je Begehung</th>
                                    <th className="py-2 pr-3 font-bold">Gültig ab</th>
                                    <th className="py-2 font-bold" />
                                </tr>
                            </thead>
                            <tbody>
                                {zustand.daten.map((z) => (
                                    <tr key={z.id} className="border-b border-slate-100">
                                        <td className="py-2 pr-3 font-semibold text-slate-800">{z.coordinatorName}</td>
                                        <td className="py-2 pr-3">
                                            <input
                                                className={`${inputClass} w-32 text-right tabular-nums`}
                                                inputMode="decimal"
                                                aria-label={`Satz für ${z.coordinatorName}`}
                                                value={bearbeitet[z.id] ?? centsAlsEingabe(z.satzJeBegehungCents)}
                                                onChange={(e) =>
                                                    setBearbeitet((prev) => ({ ...prev, [z.id]: e.target.value }))
                                                }
                                            />
                                        </td>
                                        <td className="py-2 pr-3 text-slate-600">
                                            {z.gueltigAb ? new Date(z.gueltigAb).toLocaleDateString('de-DE') : '—'}
                                        </td>
                                        <td className="py-2">
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    variant="secondary"
                                                    className="h-9 text-xs"
                                                    disabled={busy}
                                                    onClick={() => void speichern(z)}
                                                    icon={busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                                                >
                                                    Speichern
                                                </Button>
                                                <button
                                                    type="button"
                                                    title="Satz löschen"
                                                    className="rounded-lg p-2 text-slate-400 transition-all hover:bg-red-50 hover:text-red-600"
                                                    disabled={busy}
                                                    onClick={() => void entfernen(z.id)}
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </KartenRahmen>
        </div>
    );
};
